import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  alpha,
  useTheme,
  Icon,
  Paper,
  TextField,
} from "@mui/material";
import { IChartOfAccount } from "../../../../accounting.interface";
import { hasPermission } from "../../../../../auth/utils/permission.utils";
import { useAuth } from "../../../../../auth/hooks/useAuth";

interface TransferBalanceDialogProps {
  open: boolean;
  onClose: () => void;
  sourceAccount: IChartOfAccount;
  availableAccounts: IChartOfAccount[];
  onTransfer: (transferData: {
    sourceAccountId: string;
    targetAccountId: string;
    transferAmount: number;
  }) => void;
  isLoading?: boolean;
}

// Visual Account Node Component for Transfer
const TransferAccountNodeCard: React.FC<{
  account: IChartOfAccount | null;
  title: string;
  subtitle: string;
  isPlaceholder?: boolean;
  transferAmount?: number;
  isSource?: boolean;
}> = ({
  account,
  title,
  subtitle,
  isPlaceholder = false,
  transferAmount = 0,
  isSource = false,
}) => {
  const theme = useTheme();

  if (!account && isPlaceholder) {
    return (
      <Paper
        sx={{
          p: 3,
          minHeight: 140,
          border: `2px dashed ${alpha(theme.palette.divider, 0.4)}`,
          bgcolor: alpha(theme.palette.background.paper, 0.5),
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 2,
        }}
      >
        <Icon
          sx={{
            fontSize: 48,
            color: alpha(theme.palette.text.secondary, 0.3),
            mb: 1,
          }}
        >
          help_outline
        </Icon>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", textAlign: "center" }}
        >
          {title}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", textAlign: "center" }}
        >
          {subtitle}
        </Typography>
      </Paper>
    );
  }

  if (!account) return null;

  const getAccountTypeColor = (type: string) => {
    switch (type) {
      case "Asset":
        return "#10B981";
      case "Liability":
        return "#EF4444";
      case "Equity":
        return "#3B82F6";
      case "Revenue":
        return "#8B5CF6";
      case "Expense":
        return "#F59E0B";
      default:
        return theme.palette.primary.main;
    }
  };

  const accountColor = getAccountTypeColor(account.accountType);

  // Calculate new balance after transfer
  const currentBalance = account.currentBalance || 0;
  const newBalance = isSource
    ? currentBalance - transferAmount
    : currentBalance + transferAmount;

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `2px solid ${accountColor}`,
        bgcolor: alpha(accountColor, 0.05),
        position: "relative",
        minHeight: 140,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            bgcolor: accountColor,
          }}
        />
        <Typography
          variant="caption"
          sx={{ color: accountColor, fontWeight: 600 }}
        >
          {title}
        </Typography>
      </Box>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, lineHeight: 1.2 }}>
        {account.accountName}
      </Typography>

      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Code: {account.accountCode} • {account.accountType}
      </Typography>

      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Current Balance
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "primary.main" }}
            >
              {account.currency} {currentBalance.toLocaleString()}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {isSource ? "After Transfer" : "After Receiving"}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: isSource ? "warning.main" : "success.main",
              }}
            >
              {account.currency} {newBalance.toLocaleString()}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Typography
        variant="caption"
        sx={{ color: "text.secondary", mt: 1, display: "block" }}
      >
        {subtitle}
      </Typography>
    </Paper>
  );
};

export const TransferBalanceDialog: React.FC<TransferBalanceDialogProps> = ({
  open,
  onClose,
  sourceAccount,
  availableAccounts,
  onTransfer,
  isLoading = false,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [targetAccountId, setTargetAccountId] = useState("");
  const [transferAmount, setTransferAmount] = useState<number>(0);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setTargetAccountId("");
      setTransferAmount(0);
    }
  }, [open]);

  // Filter accounts that can receive transfers (leaf accounts of same type, different account)
  const eligibleAccounts = availableAccounts.filter(
    (acc) =>
      acc.id !== sourceAccount.id &&
      acc.accountType === sourceAccount.accountType &&
      acc.allowDirectPosting && // Only leaf accounts
      acc.isActive !== false // Exclude inactive accounts
  );

  const targetAccount = eligibleAccounts.find(
    (acc) => acc.id === targetAccountId
  );

  const maxTransferAmount = sourceAccount.currentBalance || 0;

  const handleTransfer = () => {
    if (!targetAccountId || transferAmount <= 0) return;

    onTransfer({
      sourceAccountId: sourceAccount.id,
      targetAccountId,
      transferAmount,
    });
  };

  const isValid =
    !!targetAccountId &&
    transferAmount > 0 &&
    transferAmount <= maxTransferAmount;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: alpha(theme.palette.secondary.main, 0.05),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Icon sx={{ color: "secondary.main", fontSize: 28 }}>swap_horiz</Icon>
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "secondary.main" }}
            >
              Transfer Balance
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5 }}
            >
              Transfer current balance between leaf accounts
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        {/* Visual Transfer Flow */}
        <Box sx={{ my: 4 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Source Account Node */}
            <Grid item xs={12} md={5}>
              <TransferAccountNodeCard
                account={sourceAccount}
                title="SOURCE ACCOUNT"
                subtitle="Current balance will be reduced"
                transferAmount={transferAmount}
                isSource={true}
              />
            </Grid>

            {/* Transfer Arrow */}
            <Grid item xs={12} md={2}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: "center",
                  justifyContent: "center",
                  py: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexDirection: { xs: "column", md: "row" },
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: 32,
                      color: "secondary.main",
                      transform: { md: "rotate(0deg)", xs: "rotate(90deg)" },
                    }}
                  >
                    trending_flat
                  </Icon>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "secondary.main",
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  >
                    TRANSFER TO
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Target Account Node or Placeholder */}
            <Grid item xs={12} md={5}>
              <TransferAccountNodeCard
                account={targetAccount || null}
                title="Select Target Account"
                subtitle="Current balance will be increased"
                isPlaceholder={!targetAccount}
                transferAmount={transferAmount}
                isSource={false}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Transfer Configuration */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Configure Transfer
          </Typography>

          <Grid container spacing={2}>
            {/* Target Account Selection */}
            <Grid item xs={12} md={8}>
              <FormControl fullWidth>
                <InputLabel>Select target account</InputLabel>
                <Select
                  value={targetAccountId}
                  onChange={(e) => setTargetAccountId(e.target.value)}
                  label="Select target account"
                  disabled={isLoading}
                >
                  {eligibleAccounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {account.accountName}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary" }}
                          >
                            {account.accountCode} • Current: {account.currency}{" "}
                            {(account.currentBalance || 0).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Transfer Amount */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Transfer Amount"
                type="number"
                value={transferAmount}
                onChange={(e) =>
                  setTransferAmount(parseFloat(e.target.value) || 0)
                }
                inputProps={{
                  min: 0,
                  max: maxTransferAmount,
                  step: 0.01,
                }}
                InputProps={{
                  startAdornment: (
                    <Typography sx={{ mr: 1, color: "text.secondary" }}>
                      {sourceAccount.currency}
                    </Typography>
                  ),
                }}
                disabled={isLoading}
                helperText={`Max: ${
                  sourceAccount.currency
                } ${maxTransferAmount.toLocaleString()}`}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Transfer Summary */}
        {targetAccount && transferAmount > 0 && (
          <Paper
            sx={{
              p: 3,
              bgcolor: alpha(theme.palette.success.main, 0.04),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              borderRadius: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: "success.main",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon sx={{ fontSize: 20 }}>calculate</Icon>
              Transfer Summary
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 2,
                    bgcolor: alpha(theme.palette.warning.main, 0.05),
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, mb: 1, color: "warning.main" }}
                  >
                    From Source
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {sourceAccount.accountName}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "warning.main" }}
                  >
                    -{sourceAccount.currency} {transferAmount.toLocaleString()}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    New balance: {sourceAccount.currency}{" "}
                    {(
                      (sourceAccount.currentBalance || 0) - transferAmount
                    ).toLocaleString()}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, mb: 1, color: "info.main" }}
                  >
                    Transfer Amount
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "info.main" }}
                  >
                    {sourceAccount.currency} {transferAmount.toLocaleString()}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    Current balance transfer
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    textAlign: "center",
                    p: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.05),
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, mb: 1, color: "success.main" }}
                  >
                    Target
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {targetAccount.accountName}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "success.main" }}
                  >
                    +{targetAccount.currency} {transferAmount.toLocaleString()}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    New balance: {targetAccount.currency}{" "}
                    {(
                      (targetAccount.currentBalance || 0) + transferAmount
                    ).toLocaleString()}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>
                  {sourceAccount.currency} {transferAmount.toLocaleString()}
                </strong>{" "}
                will be transferred from
                <strong> {sourceAccount.accountName}</strong> to{" "}
                <strong>{targetAccount.accountName}</strong>. This affects only
                the current balance, not the opening balance.
              </Typography>
            </Alert>
          </Paper>
        )}

        {eligibleAccounts.length === 0 && (
          <Alert severity="warning">
            No eligible accounts found for transfer. Balance can only be
            transferred between leaf accounts of the same type.
          </Alert>
        )}

        {transferAmount > maxTransferAmount && transferAmount > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Transfer amount cannot exceed the current balance of{" "}
            {sourceAccount.currency} {maxTransferAmount.toLocaleString()}.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 3 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleTransfer}
          disabled={
            !isValid ||
            isLoading ||
            !hasPermission(
              "accounting.chart_of_accounts.transfer_balance",
              user?.newPermissions,
              user?.assignment_details?.id,
              user?.is_store_owner
            )
          }
          startIcon={
            isLoading ? <Icon>hourglass_empty</Icon> : <Icon>swap_horiz</Icon>
          }
        >
          {isLoading ? "Transferring..." : "Transfer Balance"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
